import { Link, router } from "@inertiajs/react"
import { ChevronDown, ChevronUp } from "lucide-react"
import { useEffect, useRef, useState, type ReactNode } from "react"

import { Button } from "@/components/ui/button"
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card"
import { Checkbox } from "@/components/ui/checkbox"
import { Label } from "@/components/ui/label"
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select"
import { Textarea } from "@/components/ui/textarea"
import { AdminLayout } from "@/layouts/AdminLayout"
import type { TimeContext } from "@/lib/types"

const PAGE_SIZE = 50

interface PoiItem {
  id: number
  name: string
  position: number | null
  host_note: string | null
  is_highlighted: boolean
  time_context: TimeContext | null
}

interface Props {
  region: { id: number; name: string }
  items: PoiItem[]
  urls: {
    update: string
    region_show: string
  }
}

// ---- Expandable row panel ----

interface RowPanelProps {
  item: PoiItem
  index: number
  isFirst: boolean
  isLast: boolean
  onMoveUp: () => void
  onMoveDown: () => void
  onChange: (patch: Partial<PoiItem>) => void
}

function PoiRow({ item, index, isFirst, isLast, onMoveUp, onMoveDown, onChange }: RowPanelProps) {
  const [expanded, setExpanded] = useState(false)
  const textareaRef = useRef<HTMLTextAreaElement>(null)

  function toggle() {
    setExpanded((prev) => {
      if (!prev) {
        // autoFocus on first field when opening
        requestAnimationFrame(() => textareaRef.current?.focus())
      }
      return !prev
    })
  }

  return (
    <li className="divide-y">
      <div className="flex items-center gap-3 px-6 py-3">
        <span className="w-6 text-right text-sm tabular-nums text-muted-foreground">
          {index + 1}
        </span>
        <span className="flex-1 truncate max-w-[240px] text-sm font-medium">{item.name}</span>
        <div className="flex gap-1">
          <Button
            variant="ghost"
            size="icon"
            disabled={isFirst}
            onClick={onMoveUp}
            aria-label={`Move ${item.name} up`}
          >
            <ChevronUp className="h-4 w-4" />
          </Button>
          <Button
            variant="ghost"
            size="icon"
            disabled={isLast}
            onClick={onMoveDown}
            aria-label={`Move ${item.name} down`}
          >
            <ChevronDown className="h-4 w-4" />
          </Button>
          <Button
            variant="ghost"
            size="icon"
            onClick={toggle}
            aria-expanded={expanded}
            aria-label={expanded ? "Collapse details" : "Expand details"}
          >
            {expanded ? (
              <ChevronUp className="h-4 w-4 text-muted-foreground" />
            ) : (
              <ChevronDown className="h-4 w-4 text-muted-foreground" />
            )}
          </Button>
        </div>
      </div>

      {expanded && (
        <div className="bg-muted/30 px-6 py-3 flex flex-col gap-2">
          <div className="grid gap-1">
            <Label htmlFor={`note-${item.id}`} className="text-xs text-muted-foreground">
              Curator note
            </Label>
            <Textarea
              ref={textareaRef}
              id={`note-${item.id}`}
              value={item.host_note ?? ""}
              onChange={(e) => onChange({ host_note: e.target.value || null })}
              rows={2}
              placeholder="Optional note visible to guests…"
              className="text-xs"
            />
          </div>

          <div className="grid gap-1">
            <Label htmlFor={`time-${item.id}`} className="text-xs text-muted-foreground">
              Time context
            </Label>
            <Select
              value={item.time_context ?? "none"}
              onValueChange={(v) =>
                onChange({ time_context: v === "none" ? null : (v as TimeContext) })
              }
            >
              <SelectTrigger id={`time-${item.id}`} className="h-7 text-xs w-36">
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="none">Any time</SelectItem>
                <SelectItem value="morning">Morning</SelectItem>
                <SelectItem value="afternoon">Afternoon</SelectItem>
                <SelectItem value="evening">Evening</SelectItem>
                <SelectItem value="all_day">All day</SelectItem>
              </SelectContent>
            </Select>
          </div>

          <div className="flex items-center gap-2">
            <Checkbox
              id={`highlight-${item.id}`}
              checked={item.is_highlighted}
              onCheckedChange={(checked) => onChange({ is_highlighted: checked === true })}
              className="h-3.5 w-3.5"
            />
            <Label
              htmlFor={`highlight-${item.id}`}
              className="cursor-pointer text-xs font-normal"
            >
              Highlight
            </Label>
          </div>
        </div>
      )}
    </li>
  )
}

// ---- Main page ----

export default function RegionGuide({ region, items: initialItems, urls }: Props) {
  const [items, setItems] = useState<PoiItem[]>(initialItems)
  const [dirty, setDirty] = useState(false)
  const [saving, setSaving] = useState(false)
  const [page, setPage] = useState(1)

  useEffect(() => {
    setItems(initialItems)
  }, [initialItems])

  const totalPages = Math.ceil(items.length / PAGE_SIZE)
  const pageItems = items.slice((page - 1) * PAGE_SIZE, page * PAGE_SIZE)
  const pageOffset = (page - 1) * PAGE_SIZE

  function markDirty() {
    setDirty(true)
  }

  function moveUp(index: number) {
    if (index === 0) return
    setItems((prev) => {
      const next = [...prev]
      ;[next[index - 1], next[index]] = [next[index], next[index - 1]]
      return next
    })
    markDirty()
  }

  function moveDown(index: number) {
    if (index === items.length - 1) return
    setItems((prev) => {
      const next = [...prev]
      ;[next[index], next[index + 1]] = [next[index + 1], next[index]]
      return next
    })
    markDirty()
  }

  function updateItem(index: number, patch: Partial<PoiItem>) {
    setItems((prev) => {
      const next = [...prev]
      next[index] = { ...next[index], ...patch }
      return next
    })
    markDirty()
  }

  function saveAll() {
    setSaving(true)
    router.patch(
      urls.update,
      {
        items: items.map((item, i) => ({
          poi_id:         item.id,
          position:       i + 1,
          host_note:      item.host_note ?? null,
          is_highlighted: item.is_highlighted,
          time_context:   item.time_context ?? null,
        })),
      },
      {
        onSuccess: () => setDirty(false),
        onFinish:  () => setSaving(false),
      },
    )
  }

  return (
    <div className="mx-auto max-w-2xl space-y-6 p-6">
      <div className="flex items-center justify-between">
          <div>
            <Link
              href={urls.region_show}
              className="text-sm text-muted-foreground hover:underline"
            >
              ← Back to {region.name}
            </Link>
            <h1 className="mt-1 text-2xl font-semibold">Guide Curation Order</h1>
          </div>
          <Button onClick={saveAll} disabled={saving || !dirty || items.length === 0}>
            {saving ? "Saving…" : "Save all changes"}
          </Button>
        </div>

        <Card>
          <CardHeader className="pb-3">
            <CardTitle className="text-base">Public POIs in this region</CardTitle>
          </CardHeader>
          <CardContent className="p-0">
            {items.length === 0 ? (
              <p className="px-6 py-8 text-center text-sm text-muted-foreground">
                No approved public POIs in this region yet.
              </p>
            ) : (
              <>
                <ul className="divide-y">
                  {pageItems.map((item, pageIndex) => {
                    const absoluteIndex = pageOffset + pageIndex
                    return (
                      <PoiRow
                        key={item.id}
                        item={item}
                        index={absoluteIndex}
                        isFirst={absoluteIndex === 0}
                        isLast={absoluteIndex === items.length - 1}
                        onMoveUp={() => moveUp(absoluteIndex)}
                        onMoveDown={() => moveDown(absoluteIndex)}
                        onChange={(patch) => updateItem(absoluteIndex, patch)}
                      />
                    )
                  })}
                </ul>

                {totalPages > 1 && (
                  <div className="flex items-center justify-center gap-2 border-t px-6 py-3">
                    <Button
                      variant="ghost"
                      size="sm"
                      disabled={page === 1}
                      onClick={() => setPage((p) => p - 1)}
                    >
                      Previous
                    </Button>
                    <span className="text-xs text-muted-foreground">
                      Page {page} of {totalPages}
                    </span>
                    <Button
                      variant="ghost"
                      size="sm"
                      disabled={page === totalPages}
                      onClick={() => setPage((p) => p + 1)}
                    >
                      Next
                    </Button>
                  </div>
                )}
              </>
            )}
          </CardContent>
        </Card>
      </div>
  )
}

RegionGuide.layout = (page: ReactNode) => <AdminLayout>{page}</AdminLayout>
